/**
 * Sales insights — who to approach, and why (SALES_INSIGHTS_PLAN.md, M2).
 *
 * Four tabs over `/insights/*`:
 *   Hot list    prospects with the reasons they rank, never a hidden score (S4)
 *   Coverage    who we hold the most content for (S1)
 *   PR firms    IR/PR firms by current clients we cover, with drill-down (S2)
 *   Engagement  articles and companies by social impressions (S3)
 *
 * Accountable surface (operator console): counts, windows and provenance are
 * shown on purpose. Rows feed the existing pipeline rather than duplicating
 * it: "Add to campaign" for any sales operator; "Add to prospects" only for
 * super_admin, because the group-membership routes are super_admin-gated and
 * membership also raises the company's coverage priority.
 */
import { Fragment, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronRight, Download, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { InsightsSearch } from '../components/insights/InsightsSearch.js';
import { CompetitorTags, FirmLinks, RecencyLegend } from '../components/insights/Recency.js';
import {
  downloadInsightsCsv,
  getCoverage,
  getEngagementArticles,
  getEngagementEntities,
  getFirm,
  getFirms,
  getHotList,
  type CapBand,
  type CoverageItem,
  type FirmClient,
  type Pipeline,
} from '../lib/api/insights.js';
import { addCampaignMember, listCampaigns } from '../lib/api/campaigns.js';
import { addGroupMembers, listEntityGroups } from '../lib/api/ingress.js';

type Tab = 'hot' | 'coverage' | 'firms' | 'engagement';

const TABS: { key: Tab; label: string }[] = [
  { key: 'hot', label: 'Hot list' },
  { key: 'coverage', label: 'Coverage' },
  { key: 'firms', label: 'PR firms' },
  { key: 'engagement', label: 'Engagement' },
];

const CAP_OPTIONS: { value: string; label: string; cap: CapBand }[] = [
  { value: '250', label: 'Under A$250M', cap: 250_000_000 },
  { value: '500', label: 'Under A$500M', cap: 500_000_000 },
  { value: '1000', label: 'Under A$1B', cap: 1_000_000_000 },
  { value: 'any', label: 'Any market cap', cap: null },
];

const WINDOW_OPTIONS = [30, 90, 365];

const PROSPECT_GROUP = 'asx_coverage_prospects';

// ── formatting ───────────────────────────────────────────────────────────────

function fmtInt(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : Math.round(n).toLocaleString();
}

function fmtCap(n: number | null): string {
  if (n === null) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  return `$${Math.round(n / 1e6).toLocaleString()}M`;
}

function fmtDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—';
}

function fmtMultiple(n: number | null): string {
  return n === null ? '—' : `${n.toFixed(1)}×`;
}

async function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── shared bits ──────────────────────────────────────────────────────────────

const selectClass =
  'rounded-lg border border-grey-200 bg-surface px-2 py-1 text-xs text-grey-700 focus:outline-none focus:ring-2 focus:ring-primary-300';

function Company({ entityId, ticker, name }: { entityId: string | null; ticker: string | null; name: string | null }) {
  // Everyone gets the shareable insights page (find-and-share, 0.3.6); the
  // entity workbench is data_manager-only, so it is not linked from here.
  const label = (
    <>
      <span className="font-semibold">{ticker ?? '—'}</span>
      {name ? <span className="text-grey-500"> · {name}</span> : null}
    </>
  );
  return entityId ? (
    <Link to={`/insights/company/${entityId}`} className="hover:underline">{label}</Link>
  ) : label;
}

function Chip({ children, tone = 'grey' }: { children: React.ReactNode; tone?: 'grey' | 'primary' | 'warn' }) {
  const cls = {
    grey: 'bg-grey-100 text-grey-700',
    primary: 'bg-primary-50 text-primary-700',
    warn: 'bg-amber-50 text-amber-800',
  }[tone];
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] leading-4 ${cls}`}>{children}</span>;
}

function PipelineChips({ p }: { p: Pipeline }) {
  return (
    <div className="flex flex-wrap gap-1">
      {p.is_customer && <Chip tone="primary">Customer</Chip>}
      {p.is_prospect && <Chip tone="primary">Prospect</Chip>}
      {p.campaigns.map((c) => <Chip key={c} tone="primary">{c}</Chip>)}
    </div>
  );
}

/** Add a company to an active campaign, or (super_admin) to the prospects group. */
function AddToPipeline({ entityId, pipeline }: { entityId: string; pipeline: Pipeline }) {
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole('super_admin');
  const qc = useQueryClient();
  const [campaignId, setCampaignId] = useState('');
  const [done, setDone] = useState<string | null>(null);

  const campaigns = useQuery({
    queryKey: ['insights-active-campaigns'],
    queryFn: () => listCampaigns('active'),
    staleTime: 5 * 60_000,
  });
  const groups = useQuery({
    queryKey: ['insights-entity-groups'],
    queryFn: () => listEntityGroups(true),
    staleTime: 5 * 60_000,
    enabled: isSuperAdmin,
  });
  const prospectGroup = groups.data?.find((g) => g.group_name === PROSPECT_GROUP);

  const refresh = () => qc.invalidateQueries({ queryKey: ['insights'] });
  const toCampaign = useMutation({
    mutationFn: () => addCampaignMember(campaignId, { entity_id: entityId }),
    onSuccess: () => { setDone('Added to campaign'); refresh(); },
  });
  const toProspects = useMutation({
    mutationFn: () => addGroupMembers(prospectGroup!.group_id, [entityId]),
    onSuccess: () => { setDone('Added to prospects'); refresh(); },
  });

  if (done) return <span className="text-[11px] text-primary-700">{done}</span>;
  const available = (campaigns.data ?? []).filter((c) => !pipeline.campaigns.includes(c.name));
  const busy = toCampaign.isPending || toProspects.isPending;
  const failed = toCampaign.isError || toProspects.isError;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {available.length > 0 && (
        <>
          <select
            aria-label="Campaign"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            className={selectClass}
          >
            <option value="">Campaign…</option>
            {available.map((c) => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
          </select>
          <button
            type="button"
            disabled={!campaignId || busy}
            onClick={() => toCampaign.mutate()}
            className="rounded-full border border-grey-200 px-2 py-0.5 text-[11px] text-grey-700 hover:bg-grey-50 disabled:opacity-40"
          >
            Add
          </button>
        </>
      )}
      {isSuperAdmin && prospectGroup && !pipeline.is_prospect && !pipeline.is_customer && (
        <button
          type="button"
          disabled={busy}
          onClick={() => toProspects.mutate()}
          className="rounded-full border border-grey-200 px-2 py-0.5 text-[11px] text-grey-700 hover:bg-grey-50 disabled:opacity-40"
        >
          + Prospects
        </button>
      )}
      {busy && <Loader2 className="h-3 w-3 animate-spin text-grey-400" />}
      {failed && <span className="text-[11px] text-red-600">Failed — try again</span>}
    </div>
  );
}

function Section({ title, subtitle, children, onDownload }: {
  title: string; subtitle?: React.ReactNode; children: React.ReactNode; onDownload?: () => Promise<void>;
}) {
  const [downloading, setDownloading] = useState(false);
  return (
    <section aria-label={title} className="bg-surface rounded-xl border border-grey-200 p-4 flex flex-col gap-3">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-grey-900">{title}</h2>
          {subtitle && <p className="text-xs text-grey-500 mt-0.5">{subtitle}</p>}
        </div>
        {onDownload && (
          <button
            type="button"
            disabled={downloading}
            onClick={async () => { setDownloading(true); try { await onDownload(); } finally { setDownloading(false); } }}
            className="inline-flex items-center gap-1.5 rounded-full border border-grey-200 px-3 py-1 text-xs text-grey-700 hover:bg-grey-50 disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            {downloading ? 'Preparing…' : 'CSV'}
          </button>
        )}
      </header>
      {children}
    </section>
  );
}

function Status({ loading, error, empty }: { loading: boolean; error: unknown; empty: boolean }) {
  if (loading) return <p className="text-sm text-grey-400 py-6 text-center">Loading…</p>;
  if (error) return <p className="text-sm text-red-600 py-6 text-center">Could not load this view.</p>;
  if (empty) return <p className="text-sm text-grey-400 py-6 text-center">Nothing matches.</p>;
  return null;
}

const th = 'py-1.5 pr-3 font-medium whitespace-nowrap';
const td = 'py-1.5 pr-3 align-top';
const tdNum = `${td} tabular-nums text-right`;

// ── tabs ─────────────────────────────────────────────────────────────────────

function HotList({ cap }: { cap: CapBand }) {
  const q = useQuery({ queryKey: ['insights', 'hot', cap], queryFn: () => getHotList(cap, 100) });
  const items = q.data?.items ?? [];
  return (
    <Section
      title="Hot list"
      subtitle={<>
        Prospects (current customers excluded) with the reasons they rank, sorted by number of reasons, then
        articles in the last 90 days. {q.data && <>{q.data.count.toLocaleString()} companies have at least one reason; top 100 shown.</>}
        {q.data?.firm_note && <span className="text-amber-700"> {q.data.firm_note}</span>}
      </>}
      onDownload={async () => saveBlob(
        await downloadInsightsCsv('hot-list', { ...(cap === null ? { no_cap: true } : { max_market_cap: cap }), limit: 2000 }),
        'insights-hot-list.csv',
      )}
    >
      <Status loading={q.isLoading} error={q.error} empty={!q.isLoading && items.length === 0} />
      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-grey-500">
                <th className={th}>Company</th>
                <th className={th}>Why</th>
                <th className={`${th} text-right`}>90d</th>
                <th className={`${th} text-right`}>Mkt cap</th>
                <th className={th}>IR/PR firm</th>
                <th className={th}>Pipeline</th>
                <th className={th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.entity_id} className="border-t border-grey-100 text-grey-900">
                  <td className={td}>
                    <Company entityId={c.entity_id} ticker={c.ticker} name={c.name} />
                    <div><CompetitorTags items={c.competitors} /></div>
                  </td>
                  <td className={td}>
                    <div className="flex flex-wrap gap-1">
                      {(c.reasons ?? []).map((r) => (
                        <Chip key={r.key} tone={r.key === 'runway_low' || r.key === 'firm_switch' ? 'warn' : 'grey'}>{r.label}</Chip>
                      ))}
                    </div>
                  </td>
                  <td className={tdNum}>{c.articles_window}</td>
                  <td className={tdNum}>{fmtCap(c.market_cap)}</td>
                  <td className={`${td} text-grey-600`}><FirmLinks links={c.firms?.firm_links} /></td>
                  <td className={td}><PipelineChips p={c.pipeline} /></td>
                  <td className={td}><AddToPipeline entityId={c.entity_id} pipeline={c.pipeline} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function Coverage({ cap, windowDays }: { cap: CapBand; windowDays: number }) {
  const [includeCustomers, setIncludeCustomers] = useState(false);
  const q = useQuery({
    queryKey: ['insights', 'coverage', cap, windowDays, includeCustomers],
    queryFn: () => getCoverage(windowDays, cap, includeCustomers, 50),
  });
  const items = q.data?.items ?? [];
  return (
    <Section
      title="Coverage"
      subtitle={<>
        Companies we hold the most content for, by published articles in the last {windowDays} days. Social figures are
        Buffer impressions on those articles.
        {q.data?.firm_note && <span className="text-amber-700"> {q.data.firm_note}</span>}
        <label className="ml-2 inline-flex items-center gap-1 text-grey-600">
          <input type="checkbox" checked={includeCustomers} onChange={(e) => setIncludeCustomers(e.target.checked)} />
          include customers
        </label>
      </>}
      onDownload={async () => saveBlob(
        await downloadInsightsCsv('coverage', {
          window_days: windowDays, include_customers: includeCustomers, limit: 2000,
          ...(cap === null ? { no_cap: true } : { max_market_cap: cap }),
        }),
        `insights-coverage-${windowDays}d.csv`,
      )}
    >
      <Status loading={q.isLoading} error={q.error} empty={!q.isLoading && items.length === 0} />
      {items.length > 0 && <CoverageTable items={items} windowDays={windowDays} />}
    </Section>
  );
}

function CoverageTable({ items, windowDays }: { items: CoverageItem[]; windowDays: number }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-grey-500">
            <th className={th}>Company</th>
            <th className={`${th} text-right`}>{windowDays}d</th>
            <th className={`${th} text-right`}>All</th>
            <th className={th}>Last article</th>
            <th className={`${th} text-right`}>Mkt cap</th>
            <th className={`${th} text-right`}>Impressions</th>
            <th className={`${th} text-right`}>Runway</th>
            <th className={th}>IR/PR firm</th>
            <th className={th}>Pipeline</th>
            <th className={th}>Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.entity_id} className="border-t border-grey-100 text-grey-900">
              <td className={td}>
                <Company entityId={c.entity_id} ticker={c.ticker} name={c.name} />
                <div><CompetitorTags items={c.competitors} /></div>
              </td>
              <td className={tdNum}>{c.articles_window}</td>
              <td className={`${tdNum} text-grey-500`}>{c.articles_total}</td>
              <td className={`${td} tabular-nums text-grey-600`}>{fmtDate(c.last_published)}</td>
              <td className={tdNum}>{fmtCap(c.market_cap)}</td>
              <td className={tdNum}>{fmtInt(c.social.impressions)}</td>
              <td className={`${tdNum} ${c.runway.low ? 'text-amber-700 font-medium' : ''}`}>
                {c.runway.quarters === null ? '—' : `${c.runway.quarters.toFixed(1)}q`}
              </td>
              <td className={`${td} text-grey-600`}>
                {c.firms?.status === 'unlinked' ? <span className="text-grey-400">not linked</span>
                  : <FirmLinks links={c.firms?.firm_links} />}
                {c.firms?.firm_mix === 'likely_switch' && <> <Chip tone="warn">switch?</Chip></>}
              </td>
              <td className={td}><PipelineChips p={c.pipeline} /></td>
              <td className={td}><AddToPipeline entityId={c.entity_id} pipeline={c.pipeline} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FirmClients({ firmId, windowDays }: { firmId: string; windowDays: number }) {
  const q = useQuery({ queryKey: ['insights', 'firm', firmId, windowDays], queryFn: () => getFirm(firmId, windowDays) });
  if (q.isLoading || q.error) return <Status loading={q.isLoading} error={q.error} empty={false} />;
  const clients: FirmClient[] = q.data?.clients ?? [];
  return (
    <table className="w-full text-xs bg-grey-50">
      <thead>
        <tr className="text-left text-grey-500">
          <th className={`${th} pl-8`}>Client</th>
          <th className={`${th} text-right`}>{windowDays}d</th>
          <th className={`${th} text-right`}>All</th>
          <th className={th}>Firm seen</th>
          <th className={th}>Other firms</th>
          <th className={th}>Pipeline</th>
          <th className={th}>Action</th>
        </tr>
      </thead>
      <tbody>
        {clients.map((c) => (
          <tr key={c.entity_id} className={`border-t border-grey-100 ${c.current ? 'text-grey-900' : 'text-grey-400'}`}>
            <td className={`${td} pl-8`}>
              <Company entityId={c.entity_id} ticker={c.ticker} name={c.name} />
              <div><CompetitorTags items={c.competitors} /></div>
            </td>
            <td className={tdNum}>{c.articles_window}</td>
            <td className={`${tdNum} text-grey-500`}>{c.articles_total}</td>
            <td className={`${td} tabular-nums whitespace-nowrap`}>
              {fmtDate(c.first_seen)} → {fmtDate(c.last_seen)}{!c.current && ' (past)'}
            </td>
            <td className={`${td} text-grey-600`}>
              <FirmLinks links={c.other_firm_links} empty="—" />
              {c.firm_mix === 'likely_switch' && <> <Chip tone="warn">switch?</Chip></>}
              {c.firm_mix === 'concurrent' && <> <Chip>concurrent</Chip></>}
            </td>
            <td className={td}><PipelineChips p={c.pipeline} /></td>
            <td className={td}>{c.pipeline.is_customer ? null : <AddToPipeline entityId={c.entity_id} pipeline={c.pipeline} />}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Firms({ windowDays }: { windowDays: number }) {
  const q = useQuery({ queryKey: ['insights', 'firms', windowDays], queryFn: () => getFirms(windowDays, 200) });
  const [open, setOpen] = useState<string | null>(null);
  const items = q.data?.items ?? [];
  return (
    <Section
      title="PR / IR firms"
      subtitle={<>
        Firms ranked by how many of their current clients we cover — the channel-partner view. A client's articles
        count under every firm acting for it. "Current" = seen on a filing within a year.
        {q.data && <> {q.data.covered_companies_no_firm_observed.toLocaleString()} of {q.data.covered_companies_linked.toLocaleString()} covered companies have no firm observed.</>}
        {q.data?.caveat && <span className="block text-amber-700 mt-1">{q.data.caveat}</span>}
      </>}
      onDownload={async () => saveBlob(
        await downloadInsightsCsv('firms', { window_days: windowDays, limit: 1000 }), `insights-firms-${windowDays}d.csv`,
      )}
    >
      <Status loading={q.isLoading} error={q.error} empty={!q.isLoading && items.length === 0} />
      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-grey-500">
                <th className={th}>Firm</th>
                <th className={`${th} text-right`}>Clients we cover</th>
                <th className={`${th} text-right`}>Current clients</th>
                <th className={`${th} text-right`}>Articles {windowDays}d</th>
                <th className={`${th} text-right`}>In pipeline</th>
                <th className={`${th} text-right`}>Customers</th>
                <th className={`${th} text-right`}>Multi-firm</th>
                <th className={`${th} text-right`}>Switches?</th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <Fragment key={f.firm_id}>
                  <tr
                    className="border-t border-grey-100 text-grey-900 cursor-pointer hover:bg-grey-50"
                    onClick={() => setOpen(open === f.firm_id ? null : f.firm_id)}
                  >
                    <td className={td}>
                      <span className="inline-flex items-center gap-1">
                        {open === f.firm_id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        <Link to={`/insights/firm/${f.firm_id}`} onClick={(e) => e.stopPropagation()} className="font-medium hover:underline">{f.name ?? '—'}</Link>
                        {f.domain && <span className="text-grey-400">{f.domain}</span>}
                      </span>
                    </td>
                    <td className={`${tdNum} font-medium`}>{f.covered_current}</td>
                    <td className={tdNum}>{f.clients_current}</td>
                    <td className={tdNum}>{f.articles_window}</td>
                    <td className={tdNum}>{f.in_pipeline}</td>
                    <td className={tdNum}>{f.customers}</td>
                    <td className={tdNum}>{f.multi_firm_clients}</td>
                    <td className={tdNum}>{f.likely_switches}</td>
                  </tr>
                  {open === f.firm_id && (
                    <tr><td colSpan={8} className="p-0"><FirmClients firmId={f.firm_id} windowDays={windowDays} /></td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function Engagement({ windowDays }: { windowDays: number }) {
  const arts = useQuery({ queryKey: ['insights', 'eng-articles', windowDays], queryFn: () => getEngagementArticles(windowDays, 50) });
  const ents = useQuery({ queryKey: ['insights', 'eng-entities', windowDays], queryFn: () => getEngagementEntities(windowDays, 30) });
  return (
    <div className="space-y-4">
      <Section
        title="Companies by social impressions"
        subtitle={`Summed over their articles published in the last ${windowDays} days. "× median" compares each post with the median for its platform and masthead.`}
        onDownload={async () => saveBlob(
          await downloadInsightsCsv('engagement/entities', { window_days: windowDays, limit: 2000 }),
          `insights-engagement-companies-${windowDays}d.csv`,
        )}
      >
        <Status loading={ents.isLoading} error={ents.error} empty={!ents.isLoading && (ents.data?.items.length ?? 0) === 0} />
        {(ents.data?.items.length ?? 0) > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-grey-500">
                  <th className={th}>Company</th>
                  <th className={`${th} text-right`}>Impressions</th>
                  <th className={`${th} text-right`}>Engagements</th>
                  <th className={`${th} text-right`}>Articles</th>
                  <th className={`${th} text-right`}>Posts</th>
                  <th className={`${th} text-right`}>× median</th>
                </tr>
              </thead>
              <tbody>
                {ents.data!.items.map((e) => (
                  <tr key={e.entity_id} className="border-t border-grey-100 text-grey-900">
                    <td className={td}><Company entityId={e.entity_id} ticker={e.ticker} name={e.name} /></td>
                    <td className={tdNum}>{fmtInt(e.impressions)}</td>
                    <td className={tdNum}>{fmtInt(e.engagements)}</td>
                    <td className={tdNum}>{e.articles}</td>
                    <td className={tdNum}>{e.posts}</td>
                    <td className={`${tdNum} text-grey-600`}>{fmtMultiple(e.multiple_of_median)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Articles by social impressions"
        subtitle={arts.data?.measurement_note}
        onDownload={async () => saveBlob(
          await downloadInsightsCsv('engagement/articles', { window_days: windowDays, limit: 2000 }),
          `insights-engagement-articles-${windowDays}d.csv`,
        )}
      >
        <Status loading={arts.isLoading} error={arts.error} empty={!arts.isLoading && (arts.data?.items.length ?? 0) === 0} />
        {(arts.data?.items.length ?? 0) > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-grey-500">
                  <th className={th}>Article</th>
                  <th className={th}>Company</th>
                  <th className={th}>Published</th>
                  <th className={th}>Masthead</th>
                  <th className={`${th} text-right`}>Impressions</th>
                  <th className={`${th} text-right`}>Engagements</th>
                  <th className={`${th} text-right`}>X clicks</th>
                  <th className={`${th} text-right`}>× median</th>
                </tr>
              </thead>
              <tbody>
                {arts.data!.items.map((a) => (
                  <tr key={a.article_id} className="border-t border-grey-100 text-grey-900">
                    <td className={td}>
                      <Link to={`/articles/${a.article_id}`} className="hover:underline">{a.title ?? '(untitled)'}</Link>
                    </td>
                    <td className={td}><Company entityId={a.entity_id} ticker={a.ticker} name={null} /></td>
                    <td className={`${td} tabular-nums text-grey-600`}>{fmtDate(a.published_at)}</td>
                    <td className={`${td} text-grey-600`}>{a.mastheads.join(', ') || '—'}</td>
                    <td className={tdNum}>{fmtInt(a.impressions)}</td>
                    <td className={tdNum}>{fmtInt(a.engagements)}</td>
                    <td className={tdNum}>{fmtInt(a.clicks)}</td>
                    <td className={`${tdNum} text-grey-600`}>{fmtMultiple(a.multiple_of_median)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export function InsightsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'hot';
  const capKey = params.get('cap') ?? '500';
  const cap = (CAP_OPTIONS.find((o) => o.value === capKey) ?? CAP_OPTIONS[1]).cap;
  const windowDays = Number(params.get('window') ?? 90) || 90;

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    next.set(k, v);
    setParams(next, { replace: true });
  };

  const showCap = tab === 'hot' || tab === 'coverage';
  const showWindow = tab !== 'hot';

  return (
    <div className="space-y-4 max-w-7xl mx-auto px-4 py-6">
      <Helmet><title>Insights · Assayer</title></Helmet>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-grey-900">Insights</h1>
          <p className="text-sm text-grey-500">Who to approach, and why — from our own coverage, firms and social data.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <InsightsSearch />
          {showCap && (
            <select aria-label="Market cap" value={capKey} onChange={(e) => set('cap', e.target.value)} className={selectClass}>
              {CAP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
          {showWindow && (
            <select aria-label="Window" value={windowDays} onChange={(e) => set('window', e.target.value)} className={selectClass}>
              {WINDOW_OPTIONS.map((d) => <option key={d} value={d}>Last {d} days</option>)}
            </select>
          )}
        </div>
      </header>
      <RecencyLegend />

      <div className="flex border-b border-grey-200 overflow-x-auto" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => set('tab', t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              tab === t.key
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'hot' && <HotList cap={cap} />}
      {tab === 'coverage' && <Coverage cap={cap} windowDays={windowDays} />}
      {tab === 'firms' && <Firms windowDays={windowDays} />}
      {tab === 'engagement' && <Engagement windowDays={windowDays} />}
    </div>
  );
}
